/* eslint-disable unicorn/filename-case -- TODO rename later */
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  RadioGroup,
  RadioGroupItem,
  ScrollArea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@istari-frontend/ui";
import { istariLink, LinkData } from "@istari/istari-authflow";
import { V3Models } from "@istari/istari-client";
import { useQuery } from "@tanstack/react-query";
import { Plus, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { AppIntData, useListAppIntegrations } from "~/api/useFunctionAuth";
import { LoadingSpinner } from "~/assets/svg";
import ConnectFileForm from "~/components/ConnectFileForm";
import { connectionQueryOptions } from "~/connections/data-source/connection-query-options";
import { type NotebookCreateSection, NotebookCreateTab } from "~/notebook/feature/notebook-create-tab";
import { ConnectFormData, importLinkSchema, SchemaProps } from "~/schemas/json-schema-utils";
import { getAppLabel, getConnectedFileAppHost, supportsConnectedFiles } from "~/utils/appIntegrationLabels";
import { cn } from "~/utils/cn";

type ConnectUploadFile = {
  displayName: string;
  extension: string;
  externalIdentifier: string;
  file: File;
  tags: string[];
  resourceType: V3Models.ResourceTypeDto;
};

export type { CreateNotebookResult } from "~/notebook/feature/notebook-create-tab";

interface AddFilesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadClick: () => void;
  onConnectUpload: (filesWithMetadata: ConnectUploadFile[]) => void;
  notebookSection?: NotebookCreateSection;
}

export function AddFilesModal({
  open,
  onOpenChange,
  onUploadClick,
  onConnectUpload,
  notebookSection,
}: AddFilesModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <AddFilesModalContent
          onOpenChange={onOpenChange}
          onUploadClick={onUploadClick}
          onConnectUpload={onConnectUpload}
          notebookSection={notebookSection}
        />
      )}
    </Dialog>
  );
}

function AddFilesModalContent({
  onOpenChange,
  onUploadClick,
  onConnectUpload,
  notebookSection,
}: {
  onOpenChange: (open: boolean) => void;
  onUploadClick: () => void;
  onConnectUpload: (filesWithMetadata: ConnectUploadFile[]) => void;
  notebookSection?: NotebookCreateSection;
}) {
  // Latch the notebook tab on once the section is ever available, then keep it on. This shows the
  // tab even if the modal opened before the system context resolved (section initially undefined),
  // while avoiding the 2↔3 tab flicker when the parent briefly drops the section during post-create
  // navigation. The latest section payload is kept in a ref so callbacks and tracked-files stay current.
  const stableSectionRef = useRef<NotebookCreateSection | undefined>(notebookSection);
  const [notebookTabEnabled, setNotebookTabEnabled] = useState(notebookSection !== undefined);
  useEffect(() => {
    if (notebookSection) {
      stableSectionRef.current = notebookSection;
      setNotebookTabEnabled(true);
    }
  }, [notebookSection]);
  const stableSection = stableSectionRef.current;

  const [activeTab, setActiveTab] = useState("upload");
  const [selectedApp, setSelectedApp] = useState<AppIntData | null>(null);
  const [validForm, setValidForm] = useState(false);
  const [formData, setFormData] = useState<ConnectFormData>({ app_host: "" });
  const [formSchema, setFormSchema] = useState<SchemaProps>();
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    if (selectedApp) {
      setFormSchema(undefined); // Removes the old form so it does not blip when the new form is added.
      importLinkSchema(getConnectedFileAppHost(selectedApp.integrationType), setFormSchema);
    }
  }, [selectedApp]);

  const handleConnect = () => {
    if (!formData) {
      console.error("The file connection form is undefined");
      return;
    }
    setIsConnecting(true);
    try {
      let metadata = formData;
      const integrationData = selectedApp?.integrationData;
      if (integrationData) {
        // Integration data is combined with the metadata file because it is relevant to the connection but is configured from the App Integrations admin panel.
        metadata = { ...metadata, ...integrationData };
      }
      const metadataFile = istariLink.createLinkMetaDataFile(metadata as LinkData);
      const [displayName, extension] = metadataFile.name.split(/\.(?=[^.]+$)/);
      onConnectUpload([
        {
          displayName: displayName || metadataFile.name,
          extension: extension || "",
          externalIdentifier: "",
          file: metadataFile,
          tags: [],
          resourceType: V3Models.ResourceTypeDto.Model,
        },
      ]);
      onOpenChange(false);
    } finally {
      setIsConnecting(false);
    }
  };

  const listFilter = { page: 1, size: 100 };
  const { data: appIntegrationPages, isError, isLoading } = useListAppIntegrations(listFilter);

  // Connecting a file from a per-user integration goes nowhere until the user has linked an account
  // to it, so the connectable set is keyed by the integrations the user already has a linked account
  // for. (A shared service-account credential surfaces the same way once it's visible to the member.)
  // Only gate once the linked accounts are actually known: if the lookup is still loading or failed,
  // fall open and leave every integration selectable rather than blocking the user on missing data.
  const linkedAccountsQuery = useQuery(connectionQueryOptions.credentials());
  const linkedAccountsKnown = linkedAccountsQuery.isSuccess;
  const linkedIntegrationIds = new Set((linkedAccountsQuery.data ?? []).map((account) => account.authIntegrationId));

  const handleAppSelect = (id: string | null) => {
    if (!id) {
      setSelectedApp(null);
      setFormData({ app_host: "" });
      return;
    }
    const appIntegration = appIntegrationPages?.items.find((a) => a.id === id);
    if (appIntegration) {
      setSelectedApp(appIntegration);
      setFormData({ app_host: getConnectedFileAppHost(appIntegration.integrationType) });
    }
  };

  const handleUploadClick = () => {
    onOpenChange(false);
    onUploadClick();
  };

  const renderConnectContent = () => {
    if (isError) {
      return (
        <div className="text-destructive py-8 text-center text-sm">
          An error occurred while loading the list of 3rd party app integrations. Please try refreshing the page.
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner data-testid="loading-apps-spinner" width="30px" height="30px" />
        </div>
      );
    }
    // Auth-only providers (e.g. shared ibm_elm) have no connect-file link schema — keep them out of
    // this picker so users don't hit a dead-end form. They remain in admin catalog + credential picker.
    const connectableApps = (appIntegrationPages?.items ?? []).filter((app) =>
      supportsConnectedFiles(app.integrationType),
    );

    if (!appIntegrationPages || connectableApps.length < 1) {
      return (
        <div className="text-destructive py-8 text-center text-sm">
          No app integrations found. Ask your administrator to add app integrations for you to connect with.
        </div>
      );
    }

    if (!selectedApp) {
      return (
        <ScrollArea className="h-[355px] pr-4">
          <RadioGroup onValueChange={handleAppSelect}>
            {connectableApps.map((app) => {
              const appLabel = getAppLabel(app.integrationType);
              const AppIcon = appLabel.icon;
              // Lead with the admin-assigned name so multiple integrations of the same provider are
              // distinguishable; fall back to the provider's trademark name when it is unset.
              const displayName = app.name?.trim() || appLabel.tmName;
              const subtitle = displayName === appLabel.tmName ? undefined : appLabel.tmName;
              const unusable = linkedAccountsKnown && !linkedIntegrationIds.has(app.id);
              return (
                <label
                  key={app.id}
                  htmlFor={app.id}
                  className={cn(
                    "border-border flex items-start gap-3 rounded-lg border p-4 transition-colors",
                    unusable ? "cursor-not-allowed opacity-60" : "hover:bg-accent cursor-pointer",
                  )}
                >
                  <RadioGroupItem value={app.id} id={app.id} disabled={unusable} data-testid={`app-radio-${app.id}`} />
                  <div className="flex flex-1 items-start gap-3">
                    <span className="text-2xl">
                      <AppIcon />
                    </span>
                    <div className="flex-1">
                      <div className="text-foreground font-medium">{displayName}</div>
                      {subtitle && <div className="text-muted-foreground text-sm">{subtitle}</div>}
                      {unusable && (
                        <div className="text-muted-foreground mt-1 text-sm">Link an account in Settings first.</div>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </RadioGroup>
        </ScrollArea>
      );
    }

    const appLabel = getAppLabel(selectedApp.integrationType);
    // Show the chosen integration's own name (not just the provider label) so two integrations of the
    // same provider stay distinct in the summary; the icon still comes from the provider label.
    const appName = selectedApp.name?.trim() || appLabel.tmName;
    const AppIcon = appLabel.icon;
    return (
      <ScrollArea className="h-[355px] pr-4">
        <div className="border-border bg-muted/50 mb-3 flex items-center gap-3 rounded-lg border p-4">
          <span className="text-2xl">
            <AppIcon />
          </span>
          <div className="flex-1">
            <div className="text-foreground font-medium">{appName}</div>
            {appName !== appLabel.tmName && <div className="text-muted-foreground text-sm">{appLabel.tmName}</div>}
          </div>
          <Button variant="ghost" size="sm" onClick={() => handleAppSelect(null)}>
            Change
          </Button>
        </div>
        {formData && (
          <ConnectFileForm
            schema={formSchema}
            formData={formData}
            setFormData={setFormData}
            handleValidForm={setValidForm}
          />
        )}
      </ScrollArea>
    );
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Add Files</DialogTitle>
      </DialogHeader>
      {/* Manual activation (not Radix's default focus-activation): in the popped-out chat window the
          dialog's focus scope can land on the Connect trigger and auto-switch tabs, showing the wrong
          pane. Controlling the value keeps Upload the default until the user actually clicks a tab. */}
      <Tabs value={activeTab} onValueChange={setActiveTab} activationMode="manual" className="w-full">
        <TabsList className={`grid w-full ${notebookTabEnabled ? "grid-cols-3" : "grid-cols-2"}`}>
          <TabsTrigger value="upload" data-testid="upload-tab">
            Upload
          </TabsTrigger>
          <TabsTrigger value="connect" data-testid="connect-tab">
            Connect
          </TabsTrigger>
          {notebookTabEnabled && (
            <TabsTrigger value="notebook" data-testid="notebook-tab">
              Notebook
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="upload" className="space-y-4">
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <Upload className="text-muted-foreground h-12 w-12" />
            <p className="text-muted-foreground text-center text-sm">
              Upload files from your computer to the workspace
            </p>
            <Button onClick={handleUploadClick} data-testid="upload-files-modal-btn">
              <Plus className="size-4" />
              Choose Files
            </Button>
          </div>
        </TabsContent>
        <TabsContent value="connect" className="space-y-4">
          {renderConnectContent()}
        </TabsContent>
        {notebookTabEnabled && stableSection && (
          <TabsContent value="notebook" className="space-y-4">
            {/* Pass the ref directly so handleSubmit reads the freshest payload at click time,
                not the snapshot pinned by the render closure when the modal first opened. */}
            <NotebookCreateTab sectionRef={stableSectionRef} onClose={() => onOpenChange(false)} />
          </TabsContent>
        )}
      </Tabs>
      {selectedApp && (
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConnect} disabled={!validForm || isConnecting} data-testid="connect-submit-btn">
            Connect
          </Button>
        </DialogFooter>
      )}
    </DialogContent>
  );
}
